import d3 from 'd3';
import _ from 'lodash';
import BaseFacetView from './BaseFacetView';

export default class HistogramFacetView extends BaseFacetView {

  constructor(config, $el, margins) {
    super(config, $el, margins);
    this._title = 'Bar Chart';
    this._extent = [];
  }

  _initGetSetters() {
    super._initGetSetters();
    this.search = (value) => this._getSetter(value, '_search', this._updateData);
  }

  _updateGroups() {
    super._updateGroups();
    this._bgGroup = this._updateBackgroundGroup(this._g);
    this._dataGroup = this._updateDataGroup(this._g);
    this._labelsGroup = this._updateLabelsGroup(this._g);
  }

  _updateBackgroundGroup(parent) {
    const group = parent.selectAll('g.bg').data(['g']);
    group.enter().append('g')
      .attr('class', 'bg')
      .style('cursor', 'crosshair')
      .on('mouseleave', d => this._onMouseLeave(d));
    return group;
  }

  _updateDataGroup(parent) {
    const group = parent.selectAll('g.data').data(['g']);
    group.enter().append('g')
      .attr('class', 'data')
      .style('cursor', 'crosshair');
    return group;
  }

  _updateLabelsGroup(parent) {
    const group = parent.selectAll('g.labels').data(['g']);
    group.enter().append('g')
      .attr('class', 'labels')
      .style('pointer-events', 'none');
    return group;
  }

  _updateData() {
    if (!this._facet || !this._facet.groups()) return;
    this._data = this._facet.groups();
    this._updateScales();
  }

  // override
  _updateScales() {
    if (!this._xValue || !this._yValue || !this._data) return;

    this._xScale = this._xScaleFunction
      .domain(this._data.map(d => this._xValue(d)))
      // .rangeRoundBands([0, this._width], 0.15);
      .rangeBands([0, this._width], 0.15);

    this._yMax = d3.max(this._data, this._yValue);
    this._yScale = this._yScaleFunction.domain([0, this._yMax]).rangeRound([0, this._height]);

    this._drawChart();
  }

  _drawChart() {
    this._drawBackground();
    this._drawData();
    this._drawLabels();
  }

  // override
  _drawBackground() {
    const bgBars = this._bgGroup.selectAll('rect').data(this._data, this._xValue);

    bgBars.enter()
      .append('rect')
      .style('stroke-width', 0)
      .on('mouseover', d => this._onMouseOver(d))
      .on('mouseout', d => this._onMouseOut(d))
      .on('mousedown', d => this._onMouseDown(d))
      .on('mouseup', d => this._onMouseUp(d));

    bgBars.attr('x', d => this._xMap(d))
      .attr('y', 0)
      .attr('width', this._xScale.rangeBand())
      .attr('height', this._height)
      .style('fill', d => this._isSelected(d) ? this._selectedUpColorBg : this._upColorBg);

    bgBars.exit().remove();
  }

  // override
  _drawData() {
    const bars = this._dataGroup.selectAll('rect').data(this._data, this._xValue);

    bars.enter()
      .append('rect')
      .on('mouseover', d => this._onMouseOver(d))
      .on('mouseout', d => this._onMouseOut(d))
      .on('mousedown', d => this._onMouseDown(d))
      .on('mouseup', d => this._onMouseUp(d));

    bars.attr('x', d => this._xMap(d))
      .attr('y', d => this._height - this._yMap(d))
      .attr('height', d => this._yMap(d))
      .style('fill', d => this._isSelected(d) ? this._selectedUpColor : this._upColor)
      .attr('width', this._xScale.rangeBand());

    bars.exit().remove();
  }

  // override
  _drawLabels() {
    const labels = this._labelsGroup.selectAll('text').data(this._data, this._xValue);

    labels.enter()
      .append('text')
      .attr('class', 'taglabel')
      .style('alignment-baseline', 'middle');

    labels
      .text(d => this._xLabel(d))
      .attr('x', d => this._xMap(d) + (this._xScale.rangeBand() / 2))
      .attr('y', this._height + 8)
      .style('text-anchor', 'middle');

    labels.exit().remove();
  }

  _drawSelection(d, state) {
    let color;
    if (state === 'over') color = this._isSelected(d) ? '_selectedOverColor' : '_overColor';
    else if (state === 'down') color = this._isSelected(d) ? '_selectedDownColor' : '_downColor';
    else if (state === 'up') color = this._isSelected(d) ? '_selectedUpColor' : '_upColor';
    this._dataGroup.selectAll('rect').data([d], this._xValue).style('fill', this[color]);
    this._bgGroup.selectAll('rect').data([d], this._xValue).style('fill', this[`${color}Bg`]);

    let textFill;
    if (state === 'up') textFill = '';
    else textFill = '_selectedUpColor';
    this._labelsGroup.selectAll('text').data([d], this._xValue).style('fill', this[textFill]);
  }

  // override
  _isSelected(d) {
      if (!this._extent || this._extent.length < 2) return false;
      const startDrag = this._extent[0] > this._extent[1] ? this._extent[1] : this._extent[0];
      const endDrag = this._extent[0] > this._extent[1] ? this._extent[0] : this._extent[1];
      const val = this._xValue(d);
      return startDrag <= val && val <= endDrag;
  }

  _onMouseDown(d) {
    this._mouseDown = true;
    this._selectionMode = !this._isSelected(d);
    this._drawChart();
    this._extent[0] = this._xValue(d);
    this._onMouseOver(d);
  }

  _onMouseOver(d) {
    if (this._mouseDown) {
      this._extent[1] = this._xValue(d);
      this._drawChart();
      if (this._liveUpdate) this._onExtentSelected();
    } else {
      this._drawSelection(d, 'over');
    }
  }

  _onMouseOut(d) {
    this._drawSelection(d, 'up');
  }

  _onMouseUp() {
    if (!this._mouseDown) return;
    this._mouseDown = false;
    if (this._extent[0] == this._extent[1] && !this._selectionMode) this.clear();
    if (!this._liveUpdate) this._onExtentSelected();
  }

  _onExtentSelected() {
    if (!this._extent || !this._extent.length) this._facet.clear();
    else {
      this._extent.sort();
      this._facet.filter(this._extent);
    }
  }

  clear() {
    this._extent = [];
    super.clear();
  }

  _updateSelection(extent) {
    if (!extent || extent.length < 2) this._extent = [];
    else this._extent = extent;
  }

}



/** WEBPACK FOOTER **
 ** D:/2016/2016 - ISI/grantorino/frontend/../../../../Marco/Dropbox (ISI Foundation)/2016 - ISI/grantorino/frontend/src/js/views/sidebar/archive/facets/HistogramFacetView.es6
 **/